/**
 * Image Processing Queue
 * Serializes HEIC conversions to prevent UI blocking and concurrent processing
 */

class ProcessingSemaphore {
  private available: number;
  private queue: Array<() => void> = [];
  private activeProcessing = new Map<string, AbortController>();

  constructor(max: number) {
    this.available = max;
  }

  async acquire(key: string): Promise<AbortController> {
    // Cancel any existing processing for this key
    const existing = this.activeProcessing.get(key);
    if (existing) {
      existing.abort();
      this.activeProcessing.delete(key);
    }

    // Create new abort controller for this request
    const controller = new AbortController();
    this.activeProcessing.set(key, controller);

    if (this.available > 0) {
      this.available--;
      return controller;
    }

    await new Promise<void>((resolve) => this.queue.push(resolve));
    this.available--;
    return controller;
  }

  release(key: string): void {
    this.activeProcessing.delete(key);
    this.available++;
    const next = this.queue.shift();
    if (next) next();
  }

  cancel(key: string): void {
    const controller = this.activeProcessing.get(key);
    if (controller) {
      controller.abort();
      this.activeProcessing.delete(key);
      this.available++;
      const next = this.queue.shift();
      if (next) next();
    }
  }
}

// Global semaphore: only 1 HEIC conversion at a time (prevents UI blocking)
const processingSemaphore = new ProcessingSemaphore(1);

/**
 * Process image with serialization and cancellation support
 */
export async function processImageWithQueue<T>(
  key: string,
  processor: (signal: AbortSignal) => Promise<T>
): Promise<T> {
  const controller = await processingSemaphore.acquire(key);
  
  try {
    const result = await processor(controller.signal);
    processingSemaphore.release(key);
    return result;
  } catch (error: any) {
    processingSemaphore.release(key);
    if (error.name === 'AbortError') {
      throw new Error('Processing cancelled');
    }
    throw error;
  }
}

/**
 * Cancel processing for a specific key
 */
export function cancelProcessing(key: string): void {
  processingSemaphore.cancel(key);
}
